import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';
import InvitationPreviewModal from '../components/InvitationPreviewModal';
import { apiService } from '../services/apiService';

const invitationIcons = [
  { emoji: '💌', size: '6xl', opacity: '15' },
  { emoji: '💕', size: '4xl', opacity: '15' },
  { emoji: '✨', size: '3xl', opacity: '15' },
  { emoji: '📜', size: '8xl', opacity: '15' },
  { emoji: '🎨', size: '6xl', opacity: '15' },
  { emoji: '💖', size: '5xl', opacity: '15' }
];

const inviteStyles = [
  { 
    id: 'watercolor-floral', 
    accent: '#ec4899',
    decorative: 'watercolor',
    background: 'radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(167, 139, 250, 0.15) 0%, transparent 50%), radial-gradient(circle at 40% 20%, rgba(251, 207, 232, 0.2) 0%, transparent 40%)',
    font: 'font-serif'
  },
  { 
    id: 'gold-geometric', 
    accent: '#d97706',
    decorative: 'geometric-luxury',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)',
    font: 'font-serif'
  },
  { 
    id: 'rustic-greenery', 
    accent: '#059669',
    decorative: 'botanical-luxury',
    background: 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(5, 150, 105, 0.12) 0%, transparent 50%)',
    font: 'font-serif'
  },
  { 
    id: 'marble-elegance', 
    accent: '#475569',
    decorative: 'marble',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%, #f8fafc 100%)',
    font: 'font-sans'
  },
  { 
    id: 'vintage-lace', 
    accent: '#ea580c',
    decorative: 'lace',
    background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(251, 146, 60, 0.03) 10px, rgba(251, 146, 60, 0.03) 20px)',
    font: 'font-serif'
  },
  { 
    id: 'beach-watercolor', 
    accent: '#06b6d4',
    decorative: 'waves-luxury',
    background: 'radial-gradient(ellipse at 50% 80%, rgba(6, 182, 212, 0.15) 0%, transparent 50%), linear-gradient(180deg, #ecfeff 0%, #cffafe 100%)',
    font: 'font-sans'
  },
  { 
    id: 'rose-garden', 
    accent: '#f43f5e',
    decorative: 'roses',
    background: 'radial-gradient(circle at 30% 30%, rgba(244, 63, 94, 0.12) 0%, transparent 40%), radial-gradient(circle at 70% 70%, rgba(251, 113, 133, 0.1) 0%, transparent 40%)',
    font: 'font-serif'
  },
  { 
    id: 'art-deco', 
    accent: '#f59e0b',
    decorative: 'art-deco',
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 25%, #fde68a 50%, #fef3c7 75%, #fffbeb 100%)',
    font: 'font-serif'
  },
  { 
    id: 'minimalist-chic', 
    accent: '#1f2937',
    decorative: 'minimal',
    background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
    font: 'font-sans'
  },
  { 
    id: 'bohemian-wildflowers', 
    accent: '#a855f7',
    decorative: 'wildflowers',
    background: 'radial-gradient(circle at 20% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(251, 146, 60, 0.1) 0%, transparent 50%)',
    font: 'font-serif'
  }
];

const Invitations = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, invitation: null });
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [previewModal, setPreviewModal] = useState({ show: false, invitation: null });

  useEffect(() => {
    loadInvitations();
    setIsVisible(true);
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await apiService.invitations.getMyInvitations();
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error loading invitations:', error);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to load invitations. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (uuid) => {
    navigate(`/wedcraft-studio/invite-generator?uuid=${uuid}`);
  };

  const handleDelete = async () => {
    try {
      await apiService.invitations.delete(deleteConfirm.invitation.uuid);
      setInvitations(invitations.filter(inv => inv.uuid !== deleteConfirm.invitation.uuid));
      setDeleteConfirm({ show: false, invitation: null });
      setNotification({
        show: true,
        title: 'Invitation Deleted',
        message: '🗑️ Your invitation has been deleted successfully.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setNotification({
        show: true,
        title: 'Delete Failed',
        message: 'Failed to delete invitation. Please try again.',
        type: 'error'
      });
    }
  };

  const handleToggleFavorite = async (uuid) => {
    try {
      await apiService.invitations.toggleFavorite(uuid);
      setInvitations(invitations.map(inv => 
        inv.uuid === uuid ? { ...inv, is_favorite: !inv.is_favorite } : inv
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const renderDecorativeElements = (decorativeType, accentColor) => {
    switch (decorativeType) {
      case 'watercolor':
        return (
          <>
            <div className="absolute top-0 left-0 w-32 h-32 opacity-40">
              <div className="text-6xl">🌸</div>
              <div className="absolute top-8 left-8 text-4xl opacity-70">🌺</div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 opacity-40 transform scale-x-[-1]">
              <div className="text-6xl">🌸</div>
              <div className="absolute top-8 right-8 text-4xl opacity-70">🌺</div>
            </div>
            <div className="absolute bottom-0 left-0 text-7xl opacity-30">🌷</div>
            <div className="absolute bottom-0 right-0 text-7xl opacity-30 transform scale-x-[-1]">🌷</div>
          </>
        );
      case 'geometric-luxury':
        return (
          <>
            <div className="absolute top-4 left-4 w-20 h-20 border-t-4 border-l-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute top-4 right-4 w-20 h-20 border-t-4 border-r-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-4 left-4 w-20 h-20 border-b-4 border-l-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-4 right-4 w-20 h-20 border-b-4 border-r-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 border-2 rotate-45 opacity-15" style={{ borderColor: accentColor }} />
          </>
        );
      case 'botanical-luxury':
        return (
          <>
            <div className="absolute top-0 left-0 text-7xl opacity-35">🌿</div>
            <div className="absolute top-0 right-0 text-7xl opacity-35 transform scale-x-[-1]">🌿</div>
            <div className="absolute bottom-0 left-0 text-7xl opacity-35 transform rotate-180">🌿</div>
            <div className="absolute bottom-0 right-0 text-7xl opacity-35 transform scale-x-[-1] rotate-180">🌿</div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">🍃</div>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">🍃</div>
          </>
        );
      case 'marble':
        return (
          <>
            <div className="absolute top-0 left-0 right-0 h-px opacity-20" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <div className="absolute bottom-0 left-0 right-0 h-px opacity-20" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <div className="absolute top-2 left-2 w-12 h-12 border-t-2 border-l-2 opacity-30" style={{ borderColor: accentColor }} />
            <div className="absolute top-2 right-2 w-12 h-12 border-t-2 border-r-2 opacity-30" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-2 left-2 w-12 h-12 border-b-2 border-l-2 opacity-30" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-2 right-2 w-12 h-12 border-b-2 border-r-2 opacity-30" style={{ borderColor: accentColor }} />
          </>
        );
      case 'lace':
        return (
          <>
            <div className="absolute top-3 left-3 text-5xl opacity-35">❦</div>
            <div className="absolute top-3 right-3 text-5xl opacity-35 transform scale-x-[-1]">❦</div>
            <div className="absolute bottom-3 left-3 text-5xl opacity-35 transform rotate-180">❦</div>
            <div className="absolute bottom-3 right-3 text-5xl opacity-35 transform scale-x-[-1] rotate-180">❦</div>
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-4xl opacity-30">✿</div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-4xl opacity-30">✿</div>
          </>
        );
      case 'waves-luxury':
        return (
          <>
            <div className="absolute top-6 left-6 text-4xl opacity-35">🐚</div>
            <div className="absolute top-6 right-6 text-4xl opacity-35">🐚</div>
            <div className="absolute bottom-6 left-6 text-4xl opacity-35">⭐</div>
            <div className="absolute bottom-6 right-6 text-4xl opacity-35">⭐</div>
          </>
        );
      case 'roses':
        return (
          <>
            <div className="absolute top-0 left-0 text-6xl opacity-35">🌹</div>
            <div className="absolute top-0 right-0 text-6xl opacity-35">🌹</div>
            <div className="absolute bottom-0 left-0 text-6xl opacity-35">🌹</div>
            <div className="absolute bottom-0 right-0 text-6xl opacity-35">🌹</div>
            <div className="absolute top-12 left-12 text-3xl opacity-25">🥀</div>
            <div className="absolute top-12 right-12 text-3xl opacity-25">🥀</div>
          </>
        );
      case 'art-deco':
        return (
          <>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">◆</div>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">◆</div>
            <div className="absolute top-6 left-6 text-4xl opacity-35">✦</div>
            <div className="absolute top-6 right-6 text-4xl opacity-35">✦</div>
            <div className="absolute bottom-6 left-6 text-4xl opacity-35">✦</div>
            <div className="absolute bottom-6 right-6 text-4xl opacity-35">✦</div>
          </>
        );
      case 'minimal':
        return (
          <>
            <div className="absolute top-0 left-0 right-0 h-px opacity-25" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <div className="absolute bottom-0 left-0 right-0 h-px opacity-25" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
          </>
        );
      case 'wildflowers':
        return (
          <>
            <div className="absolute top-2 left-4 text-5xl opacity-35">🌼</div>
            <div className="absolute top-8 right-8 text-4xl opacity-30">🌻</div>
            <div className="absolute bottom-4 right-4 text-5xl opacity-35">🌺</div>
            <div className="absolute bottom-12 left-8 text-4xl opacity-30">🌷</div>
          </>
        );
      default:
        return null;
    }
  };

  const getTemplateBackground = (templateId) => {
    const templates = {
      'watercolor-floral': 'radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(167, 139, 250, 0.15) 0%, transparent 50%)',
      'gold-geometric': 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)',
      'rustic-greenery': 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
      'marble-elegance': 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%)',
      'vintage-lace': 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(251, 146, 60, 0.03) 10px)',
      'beach-watercolor': 'radial-gradient(ellipse at 50% 80%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)',
      'rose-garden': 'radial-gradient(circle at 30% 30%, rgba(244, 63, 94, 0.12) 0%, transparent 40%)',
      'art-deco': 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      'minimalist-chic': 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
      'bohemian-wildflowers': 'radial-gradient(circle at 20% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)'
    };
    return templates[templateId] || templates['watercolor-floral'];
  };

  const getTemplateAccent = (templateId) => {
    const accents = {
      'watercolor-floral': '#ec4899',
      'gold-geometric': '#d97706',
      'rustic-greenery': '#059669',
      'marble-elegance': '#475569',
      'vintage-lace': '#ea580c',
      'beach-watercolor': '#06b6d4',
      'rose-garden': '#f43f5e',
      'art-deco': '#f59e0b',
      'minimalist-chic': '#1f2937',
      'bohemian-wildflowers': '#a855f7'
    };
    return accents[templateId] || '#ec4899';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const filteredInvitations = invitations
    .filter(inv => filter === 'all' || (filter === 'favorites' && inv.is_favorite))
    .filter(inv => 
      searchQuery === '' || 
      inv.bride_names?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.groom_names?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const getDashboardStats = () => {
    return {
      total: invitations.length,
      favorites: invitations.filter(inv => inv.is_favorite).length,
      recent: invitations.filter(inv => {
        const createdDate = new Date(inv.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return createdDate > weekAgo;
      }).length,
      templates: new Set(invitations.map(inv => inv.template_id)).size
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <BackgroundIcons icons={invitationIcons} />
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce">
            <span className="text-4xl animate-pulse">💌</span>
          </div>
          <p className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
            Loading your invitations...
          </p>
        </div>
      </div>
    );
  }

  const stats = getDashboardStats();

  // Empty State
  if (invitations.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
        <BackgroundIcons icons={invitationIcons} />
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
              <span className="text-4xl animate-pulse">💌</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide">
              No Invitations Yet
            </h1>
            
            <div className="flex items-center justify-center mb-6">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="mx-4 text-3xl animate-bounce">💕</div>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            </div>
            
            <p className="text-2xl text-gray-700 font-light italic mb-8">
              Create your first beautiful wedding invitation ✨
            </p>
            
            <Link to="/wedcraft-studio/invite-generator">
              <button className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-12 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse">
                <span className="relative z-10 flex items-center justify-center">
                  <span className="mr-2 text-xl group-hover:animate-bounce">✨</span>
                  Create First Invitation
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </button>
            </Link>
          </div>
        </div>

        <NotificationModal
          isOpen={notification.show}
          onClose={() => setNotification({ ...notification, show: false })}
          title={notification.title}
          message={notification.message}
          type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={invitationIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* Beautiful Header - VowAI Style */}
        <div className="text-center mb-16 relative">
          
          {/* Back to Dashboard Button */}
          <div className="absolute top-0 left-0">
            <Link 
              to="/dashboard"
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl group-hover:animate-bounce">←</span>
              Back to Dashboard
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </Link>
          </div>
          
          <div className={`inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <span className="text-4xl animate-pulse">💌</span>
          </div>
          
          <h1 className={`text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            My Invitations
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">💕</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>
          
          <p className={`text-2xl text-gray-700 font-light italic transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Manage your beautiful wedding invitations ✨
          </p>
          
          <div className="mt-8">
            <span className="px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full font-bold text-lg shadow-xl animate-pulse">
              {stats.total} Invitation{stats.total !== 1 ? 's' : ''} Created
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-rose-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Invitations</p>
                <p className="text-3xl font-bold text-rose-600">{stats.total}</p>
              </div>
              <div className="bg-rose-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">💌</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-gold-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Favorites</p>
                <p className="text-3xl font-bold text-gold-600">{stats.favorites}</p>
              </div>
              <div className="bg-gold-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">⭐</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Recent (7 days)</p>
                <p className="text-3xl font-bold text-purple-600">{stats.recent}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🆕</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-pink-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Templates Used</p>
                <p className="text-3xl font-bold text-pink-600">{stats.templates}</p>
              </div>
              <div className="bg-pink-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🎨</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className={`bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-6 mb-8 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Filters */}
            <div className="flex gap-4">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                  filter === 'all'
                    ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-rose-50 border border-rose-200'
                }`}
              >
                All ({invitations.length})
              </button>
              <button
                onClick={() => setFilter('favorites')}
                className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                  filter === 'favorites'
                    ? 'bg-gradient-to-r from-gold-500 to-yellow-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gold-50 border border-gold-200'
                }`}
              >
                ⭐ Favorites ({stats.favorites})
              </button>
            </div>

            {/* Search and Create */}
            <div className="flex gap-4 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <span className="text-xl">🔍</span>
                </div>
                <input
                  type="search"
                  placeholder="Search invitations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-white"
                />
              </div>
              <Link to="/wedcraft-studio/invite-generator">
                <button className="group relative overflow-hidden bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
                  <span className="relative z-10 flex items-center">
                    <span className="mr-2 group-hover:animate-bounce">+</span>
                    Create New
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-emerald-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Invitations Grid */}
        {filteredInvitations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {searchQuery ? 'No invitations found' : 'No invitations in this filter'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery ? 'Try a different search term' : 'Create your first invitation to get started!'}
            </p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {filteredInvitations.map((invitation) => (
              <div 
                key={invitation.uuid}
                onClick={() => setPreviewModal({ show: true, invitation })}
                className="group relative bg-gradient-to-br from-white via-rose-50/30 to-pink-50/30 backdrop-blur-lg rounded-3xl shadow-xl border-2 border-transparent hover:border-rose-300 overflow-hidden hover:shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer"
              >
                {/* Animated Gradient Border Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-3xl"></div>
                
                {/* Decorative Floating Elements */}
                <div className="absolute top-4 right-4 text-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse">💌</div>
                <div className="absolute bottom-4 left-4 text-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse delay-100">✨</div>
                
                {/* Preview Thumbnail with Enhanced Styling */}
                <div 
                  className="h-56 p-6 relative overflow-hidden"
                  style={{ background: getTemplateBackground(invitation.template_id) }}
                >
                  {/* Shimmer Effect on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <div className="text-center relative z-10">
                    <div 
                      className="text-2xl font-serif font-bold mb-3 group-hover:scale-105 transition-transform duration-300"
                      style={{ color: getTemplateAccent(invitation.template_id) }}
                    >
                      {invitation.bride_names} & {invitation.groom_names}
                    </div>
                    
                    <div className="inline-block px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg mb-2">
                      <div className="text-sm font-semibold text-gray-700">
                        {formatDate(invitation.wedding_date)}
                      </div>
                    </div>
                    
                    {invitation.venue && (
                      <div className="text-sm text-gray-600 mt-3 font-medium">
                        📍 {invitation.venue}
                      </div>
                    )}
                  </div>
                  
                  {invitation.is_favorite && (
                    <div className="absolute top-3 right-3 text-3xl animate-bounce drop-shadow-lg">⭐</div>
                  )}
                </div>

                {/* Card Info with Enhanced Design */}
                <div className="p-5 relative z-10 bg-white/60 backdrop-blur-sm">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold text-white capitalize px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full shadow-md group-hover:shadow-lg transition-shadow duration-300">
                        {invitation.template_id?.replace(/-/g, ' ') || 'Custom'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <span>🕐</span>
                      <span>{new Date(invitation.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Actions with Enhanced Styling */}
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewModal({ show: true, invitation });
                      }} 
                      className="group/viewbtn flex-1 relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-2 px-4 rounded-xl font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <span className="mr-1 group-hover/viewbtn:animate-bounce">✨</span>
                        View
                      </span>
                      {/* VowAI signature glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(invitation.uuid);
                      }} 
                      className="group/edit flex-1 relative overflow-hidden bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white py-2 px-4 rounded-xl font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <span className="mr-1 group-hover/edit:animate-bounce">✏️</span>
                        Edit
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover/edit:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(invitation.uuid);
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-gold-200 to-yellow-200 hover:from-gold-300 hover:to-yellow-300 rounded-xl transition-all duration-300 text-xl border-2 border-gold-300 transform hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg"
                    >
                      {invitation.is_favorite ? '⭐' : '☆'}
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ show: true, invitation });
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-red-200 to-rose-200 hover:from-red-300 hover:to-rose-300 text-red-700 rounded-xl transition-all duration-300 text-xl border-2 border-red-300 transform hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg font-bold"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Magical Glow Effect on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-400/0 via-pink-400/0 to-gold-400/0 group-hover:from-rose-400/10 group-hover:via-pink-400/10 group-hover:to-gold-400/10 transition-all duration-500 rounded-3xl pointer-events-none"></div>
              </div>
            ))}
          </div>
        )}
        
      </div>

      {/* Preview Modal */}
      <InvitationPreviewModal
        isOpen={previewModal.show}
        onClose={() => setPreviewModal({ show: false, invitation: null })}
        invitation={previewModal.invitation}
        templateId={previewModal.invitation?.template_id}
        inviteStyles={inviteStyles}
        renderDecorativeElements={renderDecorativeElements}
      />

      {/* Modals */}
      <NotificationModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, invitation: null })}
        type="warning"
        title="Delete Invitation?"
        message="Are you sure you want to delete this invitation? This action cannot be undone."
        isConfirmation={true}
        onConfirm={handleDelete}
        confirmText="Yes, Delete"
        cancelText="Cancel"
      />

      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
      />
    </div>
  );
};

export default Invitations;
